import React, { useEffect, useRef, useState } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Row,
  Col,
  Label,
  Input,
  Button,
  Spinner,
  Collapse,
} from 'reactstrap';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { updatePurifierOrderDetails } from '../actions/action';
import { getModelList } from '../../ServiceOrders/ManageServiceOrders/actions/action';
import AddAddress from '../../Orders/Cart/components/AddAddress';
import { ErrorToast, SuccessToast } from '../../../utils/Toaster';

const initialForm = {
  ls_order_no: '',
  model_no: '',
  sku: '',
  serial_no: '',
  warranty: '',
  customer_name: '',
  mobile: '',
  email: '',
  building: '',
  flat: '',
  floor: '',
  street: '',
  land_mark: '',
  area: '',
  emirate: '',
  latitude: '',
  longitude: '',
  installation_date: '',
  warranty_end_date: '',
  amc_start_date: '',
  amc_end_date: '',
};

const PurifierCreateOffcanvas = ({ isOpen, toggle, onCreated }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(initialForm);
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isAddressOpen, setIsAddressOpen] = useState(true);
  const addressFormRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setForm(initialForm);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getModelList();
        const raw = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const seen = new Set();
        const uniq = [];
        raw.forEach((m) => {
          const name = m?.name;
          if (!name || seen.has(name)) return;
          seen.add(name);
          uniq.push({ name, sku: m?.sku || '' });
        });
        if (!cancelled) setModels(uniq);
      } catch (e) {
        console.error('Failed to load purifier models', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleModelChange = (e) => {
    const name = e.target.value;
    const matched = models.find((m) => m.name === name);
    setForm((prev) => ({
      ...prev,
      model_no: name,
      sku: matched?.sku || '',
    }));
  };

  const handleAddressSet = (a) => {
    setForm((prev) => ({
      ...prev,
      building: a.buildingName || '',
      street: a.streetName || '',
      flat: a.flatNo || '',
      floor: a.floorNumber || '',
      area: a.areaName || '',
      land_mark: a.nearestLandMark || '',
      latitude: a.latitude || '',
      longitude: a.longitude || '',
      emirate: a.region || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.model_no) {
      ErrorToast('Please select a model');
      return;
    }
    if (!form.customer_name || !form.mobile) {
      ErrorToast('Customer name and mobile are required');
      return;
    }
    setSaving(true);
    try {
      const result = await updatePurifierOrderDetails({
        params: { ...form, country: 'ae' },
        dispatch,
      });
      if (result?.code === 200) {
        SuccessToast(result?.message || 'Purifier order created.');
        if (onCreated) onCreated();
        toggle();
      } else {
        ErrorToast(result?.message || 'Failed to create purifier order.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '50vw', maxWidth: '800px' }}
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom">
        <span className="fw-bold">Create Purifier Order</span>
      </OffcanvasHeader>
      <OffcanvasBody>
        <form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Label className="form-label">LS Order No</Label>
              <Input
                type="text"
                name="ls_order_no"
                value={form.ls_order_no}
                onChange={handleChange}
                placeholder="(optional — leave blank to auto-generate)"
              />
            </Col>
            <Col md={6}>
              <Label className="form-label">Warranty</Label>
              <Input
                type="text"
                name="warranty"
                value={form.warranty}
                onChange={handleChange}
              />
            </Col>
            <Col md={6}>
              <Label className="form-label">Model No <span className="text-danger">*</span></Label>
              <Input
                type="select"
                name="model_no"
                value={form.model_no}
                onChange={handleModelChange}
                required
              >
                <option value="">Select model</option>
                {models.map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6}>
              <Label className="form-label">SKU</Label>
              <Input
                type="text"
                name="sku"
                value={form.sku}
                readOnly
                style={{ backgroundColor: '#e9ecef' }}
              />
            </Col>
            <Col md={6}>
              <Label className="form-label">Serial No</Label>
              <Input
                type="text"
                name="serial_no"
                value={form.serial_no}
                onChange={handleChange}
              />
            </Col>

            <Col md={12}>
              <hr className="my-2" />
              <h6 className="text-muted text-uppercase fw-bold mb-2">Customer</h6>
            </Col>
            <Col md={6}>
              <Label className="form-label">Customer Name <span className="text-danger">*</span></Label>
              <Input
                type="text"
                name="customer_name"
                value={form.customer_name}
                onChange={handleChange}
                required
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">Mobile <span className="text-danger">*</span></Label>
              <Input
                type="text"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                required
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">Email</Label>
              <Input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </Col>

            <Col md={12}>
              <hr className="my-2" />
              <div
                className="d-flex justify-content-between align-items-center"
                onClick={() => setIsAddressOpen((p) => !p)}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="text-muted text-uppercase fw-bold mb-0">
                  <i className="bx bx-map-pin me-1"></i>
                  Address Details
                </h6>
                <i
                  className={`bx ${isAddressOpen ? 'bx-chevron-up' : 'bx-chevron-down'}`}
                ></i>
              </div>
            </Col>
            <Col md={12}>
              <Collapse isOpen={isAddressOpen}>
                <div className="border rounded-3 bg-white shadow-sm overflow-hidden">
                  <AddAddress
                    service_order_no={null}
                    showmapandAddress={true}
                    setShowmapandAddress={() => {}}
                    source="serviceOrder"
                    setAddress={handleAddressSet}
                    hideSubmit={true}
                    formRef={addressFormRef}
                    country="ae"
                  />
                </div>
              </Collapse>
            </Col>

            <Col md={12}>
              <hr className="my-2" />
              <h6 className="text-muted text-uppercase fw-bold mb-2">
                Installation / Warranty / AMC
              </h6>
            </Col>
            <Col md={3}>
              <Label className="form-label">Installation Date</Label>
              <Input
                type="date"
                name="installation_date"
                value={form.installation_date}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">Warranty End Date</Label>
              <Input
                type="date"
                name="warranty_end_date"
                value={form.warranty_end_date}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">AMC Start Date</Label>
              <Input
                type="date"
                name="amc_start_date"
                value={form.amc_start_date}
                onChange={handleChange}
              />
            </Col>
            <Col md={3}>
              <Label className="form-label">AMC End Date</Label>
              <Input
                type="date"
                name="amc_end_date"
                value={form.amc_end_date}
                onChange={handleChange}
              />
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button outline color="secondary" onClick={toggle} disabled={saving}>
              Cancel
            </Button>
            <Button color="success" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </div>
        </form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default PurifierCreateOffcanvas;
